import SectionTitle from "../common/SectionTitle";
import { ShieldCheck, Lock, Cookie, Link2, RefreshCw, UserCheck, FileEdit, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          title="Privacy Policy"
          subtitle="Floret Town - Flower | Fragrance | Green"
        />

        <div className="mt-10 bg-white rounded-2xl shadow-xl p-8 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center border border-emerald-100">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Privacy Policy
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                At Floret Town, we are committed to protecting and respecting
                the privacy of our website visitors, customers, and
                stakeholders. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you visit our
                website or interact with us.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="grid grid-cols-1 gap-6">
            {/* 1 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Lock className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  1. Information We Collect
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                We may collect the following types of information:
              </p>

              <p className="mt-2 text-sm text-gray-600 font-semibold">
                a) Personal Information
              </p>
              <ul className="mt-1 list-disc pl-6 space-y-1 text-sm text-gray-600">
                <li>Full name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>
                  Enquiry details or messages submitted through contact forms
                </li>
              </ul>

              <p className="mt-2 text-sm text-gray-600 font-semibold">
                b) Non-Personal Information
              </p>
              <ul className="mt-1 list-disc pl-6 space-y-1 text-sm text-gray-600">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>
                  Pages visited, time spent on the site, and referring URLs
                </li>
              </ul>
            </div>

            {/* 2 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <ShieldCheck className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  2. How We Use Your Information
                </h3>
              </div>

              <ul className="mt-3 list-disc pl-6 space-y-1 text-sm text-gray-600">
                <li>To respond to enquiries and requests</li>
                <li>
                  To provide information about our projects, services, and
                  offerings
                </li>
                <li>
                  To communicate marketing or promotional updates (where
                  permitted)
                </li>
                <li>
                  To improve website functionality, content, and user experience
                </li>
                <li>To analyze website usage and trends</li>
              </ul>
            </div>

            {/* 3 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Lock className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  3. Data Protection and Security
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                We implement reasonable administrative, technical, and
                organizational measures to safeguard your personal information
                against unauthorized access, alteration, disclosure, or
                destruction. While we strive to protect your data, no method of
                transmission over the internet or electronic storage is
                completely secure. Therefore, we cannot guarantee absolute
                security of information.
              </p>
            </div>

            {/* 4 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Cookie className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  4. Sharing and Disclosure
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                Floret Town does not sell, rent, or trade your personal
                information to third parties. We may share information only in
                the following circumstances:
              </p>

              <ul className="mt-3 list-disc pl-6 space-y-1 text-sm text-gray-600">
                <li>
                  With trusted service providers who assist in operating our
                  website or business
                </li>
                <li>
                  When required to comply with legal obligations, court orders,
                  or regulations
                </li>
                <li>
                  To protect the rights, property, or safety of Floret Town, its
                  users, or others
                </li>
              </ul>
            </div>

            {/* 5 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Link2 className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  5. Cookies and Tracking Technologies
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                Our website may use cookies and similar technologies to enhance
                user experience, analyze traffic, and improve content &
                functionality. You may choose to disable cookies through your
                browser settings. Please note that disabling cookies may affect
                certain features of the website.
              </p>
            </div>

            {/* 6 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <RefreshCw className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  6. Third-Party Links & Updates
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                Our website may contain links to third-party websites. Floret
                Town is not responsible for the privacy practices, content, or
                policies of such external websites. We encourage users to review
                the privacy policies of any third-party sites they visit.
              </p>
            </div>

            {/* 7 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <UserCheck className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  7. User Consent
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                By using this website and submitting your information, you
                consent to the collection and use of information in accordance
                with this Privacy Policy.
              </p>
            </div>
            {/* 8 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <FileEdit className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  8. Changes to This Privacy Policy
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                Floret Town reserves the right to update or modify this Privacy
                Policy at any time. Any changes will be effective immediately
                upon posting on this page. Users are encouraged to review this
                policy periodically to stay informed of updates.
              </p>
            </div>
            {/* 6 */}
            <div className="rounded-2xl border border-gray-100 p-6 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Mail className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  9. Contact Information
                </h3>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or the handling of your personal information,
                please contact us through the contact details provided on our
                website.
              </p>
            </div>
          </div>

          <p className="pt-4 text-sm text-gray-500">
            Last updated: {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
